import type { KeyboardEvent } from 'react'
import { Bell, Check } from 'lucide-react'
import { SALON_NOTE_MAX, type SalonNote } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDay, reminderWhen } from '../reminders.ts'

// Small pieces shared by the comments dialog, a single comment and the reminder window.

export const textareaClass =
  'w-full resize-y rounded-control border border-line-strong bg-surface-soft px-3.5 py-2.5 text-[16px] text-ink outline-none placeholder:text-ink-muted focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-ring)] md:text-[14.5px]'

export const linkButtonClass = 'min-h-11 font-medium text-ink-muted hover:text-ink hover:underline md:min-h-6'

/** The counter appears only near the limit: most comments are a line or two. */
const COUNTER_FROM = SALON_NOTE_MAX - 200

export const errorText = (error: unknown): string =>
  error instanceof ApiError ? error.message : 'Не получилось — попробуйте ещё раз.'

/** ⌘/Ctrl + Enter sends, a plain Enter is a new line. */
export const submitOnModEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
    event.preventDefault()
    event.currentTarget.form?.requestSubmit()
  }
}

export function Counter({ length }: { length: number }) {
  if (length < COUNTER_FROM) return null
  return (
    <span className="tabular text-[12px] text-ink-faint">
      {length}/{SALON_NOTE_MAX}
    </span>
  )
}

interface ReminderLineProps {
  note: SalonNote
  today: string
  /** The signed-in person wrote the note: «вам» instead of the author's name. */
  own: boolean
  className?: string
}

/** «Напоминание вам: завтра» — or whose it is, or that it was done. */
export function ReminderLine({ note, today, own, className }: ReminderLineProps) {
  if (!note.remindOn) return null

  if (note.reminderDoneAt) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-[12.5px] text-ink-faint', className)}>
        <Check className="size-3.5 shrink-0" aria-hidden />
        Напоминание на {formatDay(note.remindOn)} выполнено
      </span>
    )
  }

  const due = note.remindOn <= today
  const whom = own ? 'вам' : `для ${note.authorName}`
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-[12.5px]',
        due ? 'font-medium text-warn-ink' : 'text-ink-muted',
        className,
      )}
    >
      <Bell className="size-3.5 shrink-0" aria-hidden />
      Напоминание {whom}: {reminderWhen(note.remindOn, today)}
    </span>
  )
}
